import React, { useState } from "react"
import { Reaction } from "@/schema"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@ui/shad/tooltip"
import { cn } from "@ui/utils"

import { useFetchUsers } from "@/hooks/api/users/queries"
import { EmojiPickerPopover } from "@/components/emoji-picker-popover"

interface ReactionBarProps {
  reactions: Reaction[]
  currentUserId: string
  onReaction: (emoji: string) => void
  isLoading?: boolean
}

interface GroupedReaction {
  emoji: string
  count: number
  hasReacted: boolean
  userIds: string[]
}

export function ReactionBar({ reactions, currentUserId, onReaction, isLoading = false }: ReactionBarProps) {
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false)
  const { data: usersData } = useFetchUsers()

  const groupedReactions = React.useMemo(() => {
    const groups: Record<string, GroupedReaction> = {}

    reactions.forEach((reaction) => {
      if (!groups[reaction.emoji]) {
        groups[reaction.emoji] = {
          emoji: reaction.emoji,
          count: 0,
          hasReacted: false,
          userIds: [],
        }
      }

      groups[reaction.emoji].count++
      groups[reaction.emoji].userIds.push(reaction.user_id)
      if (reaction.user_id === currentUserId) {
        groups[reaction.emoji].hasReacted = true
      }
    })

    return Object.values(groups).sort((a, b) => b.count - a.count)
  }, [reactions, currentUserId])

  const getUserNames = (userIds: string[]) => {
    if (!usersData?.users) return []

    return userIds
      .map((userId) => {
        const user = usersData.users.find((u) => u.id === userId)
        return user?.full_name || "Unknown user"
      })
      .filter(Boolean)
  }

  const handleEmojiSelect = (emoji: string) => {
    onReaction(emoji)
    setIsEmojiPickerOpen(false)
  }

  return (
    <TooltipProvider>
      <div className="flex flex-wrap items-center gap-1.5">
        {groupedReactions.map((group) => {
          const userNames = getUserNames(group.userIds)

          let formattedNames = ""
          if (userNames.length > 0) {
            if (userNames.length === 1) {
              formattedNames = userNames[0]
            } else if (userNames.length === 2) {
              formattedNames = `${userNames[0]} and ${userNames[1]}`
            } else {
              const lastUser = userNames[userNames.length - 1]
              const otherUsers = userNames.slice(0, -1).join(", ")
              formattedNames = `${otherUsers} and ${lastUser}`
            }
          }

          return (
            <Tooltip key={group.emoji}>
              <TooltipTrigger asChild>
                <button
                  className={cn(
                    "inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs font-medium transition-colors",
                    "hover:bg-muted",
                    group.hasReacted
                      ? "border-blue-foreground bg-blue-background text-blue-foreground"
                      : "border-border bg-gray-background text-gray-foreground"
                  )}
                  onClick={() => onReaction(group.emoji)}
                  disabled={isLoading}
                >
                  <span className="text-[13px]">{group.emoji}</span>
                  <span>{group.count}</span>
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-48 text-xs">
                  {formattedNames && (
                    <>
                      <span className="text-background font-medium">{formattedNames}</span>
                      <span className="text-tertiary"> reacted with {group.emoji}</span>
                    </>
                  )}
                </p>
              </TooltipContent>
            </Tooltip>
          )
        })}

        <EmojiPickerPopover
          open={isEmojiPickerOpen}
          onOpenChange={setIsEmojiPickerOpen}
          onEmojiSelect={handleEmojiSelect}
        />
      </div>
    </TooltipProvider>
  )
}
